/**
 * New Year table cards — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const newYearTableCardsPack: ToilePack = {
    slug: 'new-year-table-cards',
    category: 'season',
    name: 'New Year table cards',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1216, h: 832},
    readings: [
        {key: 'tent', label: 'Tent card', subject: 'A horizontal fold-over place card with a gold star in the corner and the top lettered exactly "Cheers to the New Year," a blank centre below for the name'},
        {key: 'flat', label: 'Flat card', subject: 'A flat rectangular place card with a small clock motif and the foot lettered exactly "Happy New Year," a ruled blank line for the name, a thin border'},
        {key: 'drink', label: 'Drink label', subject: 'A tent card for the bar with a champagne motif and the foot lettered exactly "Please help yourself," a blank centre for the drink name'},
    ],
    styles: [
        {key: 'glam', label: 'Black and gold', style: 'black-and-gold new year printable, confetti borders, elegant serif lettering, print-ready', palette: 'Black, gold and champagne cream', framing: 'Laid out on a dark page with a gold border and a margin'},
        {key: 'confetti', label: 'Confetti party', style: 'bright confetti party printable, streamers, rounded bold lettering, print-ready', palette: 'Gold, pink, teal and white', framing: 'Laid out on a vertical page with a confetti border and a white margin'},
        {key: 'minimal', label: 'Minimal', style: 'minimal new year printable, thin lines, generous white space, print-ready', palette: 'Black and gold on white', framing: 'Laid out on a clean page with wide margins'},
    ],
    directions: ['for a black-tie gala instead', 'with the year printed large', 'as a folded drink-charm tag'],
    directionHint: 'try: the year printed large',
    seo: {
        title: 'New Year Table Cards: Tent, Flat & Drink Labels',
        metaDescription: 'Nine New Year table cards — a tent place card, a flat place card, and a drink label — blank for the name, in black-and-gold, confetti, minimal.',
        h1: 'New Year table cards: tent, flat, and drink labels',
        tagline: 'Nine cards, three formats, three styles each — greeting lettered, blank for the name.',
        intro: 'Three New Year table cards — a fold-over tent card, a flat place card, and a bar drink label — each with a greeting lettered and a blank line or center left for a name, in black-and-gold, confetti-party, and minimal styles. Ask for a different fold, a place-number version, or a matching menu set, and the same card grammar carries it.',
        useCases: [
            "a New Year's Eve dinner table setting",
            'a home cocktail bar',
            'a formal party place card',
            'a family countdown table',
        ],
        faq: [
            {q: 'Are the names already printed on the cards?', a: 'No — every card leaves a blank line or center for you to write the name by hand.'},
            {q: 'What size do these print at?', a: 'Each card is a single page sized to fold or trim down to a standard place-card size.'},
            {q: 'Can I get a place-number version instead of names?', a: 'Yes — the direction field takes any variation, including numbers instead of names.'},
            {q: 'What are the three styles?', a: 'Black-and-gold, bright confetti-party, and minimal thin-line.'},
        ],
    },
}
